import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { LoginCredentials, RegisterData } from '@/types/user';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authStore = useAuthStore();
  const addNotification = useNotificationStore((state) => state.addNotification);

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => authService.login(credentials),
    onSuccess: async (data) => {
      await authStore.login(data.user, data.tokens);
      addNotification({
        type: 'success',
        title: 'Login Successful',
        message: `Welcome back, ${data.user.name}!`,
      });
      navigate('/tickets');
    },
    onError: () => {
      addNotification({
        type: 'error',
        title: 'Login Failed',
        message: 'Invalid email or password',
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (data: RegisterData) => authService.register(data),
    onSuccess: async (data) => {
      await authStore.login(data.user, data.tokens);
      addNotification({
        type: 'success',
        title: 'Registration Successful',
        message: `Welcome, ${data.user.name}!`,
      });
      navigate('/tickets');
    },
    onError: () => {
      addNotification({
        type: 'error',
        title: 'Registration Failed',
        message: 'Unable to create account. Please try again.',
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      queryClient.clear();
      authStore.logout();
      addNotification({
        type: 'success',
        title: 'Logged Out',
        message: 'You have been successfully logged out.',
      });
      navigate('/login');
    },
  });

  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authService.getCurrentUser(),
    enabled: authStore.isAuthenticated,
    retry: false,
  });

  return {
    user: authStore.user,
    isAuthenticated: authStore.isAuthenticated,
    isLoading: authStore.isLoading || isLoadingUser,
    currentUser,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
  };
}
